import React, { useEffect } from 'react'
import type { ParagraphBlockFontLookup } from '@/lib/text/types/paragraph-block-font-lookup'
import { useFonts } from '@/providers/font'
import type { FontFamily } from '@/api/types/font-family'

/**
 * `FontProvider` is rendered by `HomeInner` as a JSX child, so `HomeInner`'s own closures
 * (e.g. `redistributeChainStory`, `handleAddCaption`) are not inside that context and can't call
 * `useFonts()` directly. This bridge is mounted inside `<FontProvider>` and syncs the font lookup
 * (and the raw family catalog) into refs `HomeInner` can read, mirroring how
 * `MissingFontsChecker`/`BlockedFontsChecker` already reach into `HomeInner` state via refs from
 * the other direction.
 */
export function TextChainFontsBridge({
  fontLookupRef,
  familiesRef,
}: {
  fontLookupRef: React.MutableRefObject<ParagraphBlockFontLookup | null>
  familiesRef?: React.MutableRefObject<FontFamily[]>
}) {
  const {
    getFamilyCssName,
    getVariantByFontId,
    getFontIdByVariant,
    getFamilyIdByCssName,
    families,
  } = useFonts()

  useEffect(() => {
    fontLookupRef.current = {
      getFamilyCssName,
      getVariantByFontId,
      getFontIdByVariant,
      getFamilyIdByCssName,
    }

    return () => {
      fontLookupRef.current = null
    }
  }, [
    fontLookupRef,
    getFamilyCssName,
    getVariantByFontId,
    getFontIdByVariant,
    getFamilyIdByCssName,
  ])

  useEffect(() => {
    if (!familiesRef) return
    familiesRef.current = families

    return () => {
      familiesRef.current = []
    }
  }, [familiesRef, families])

  return null
}
